import { MaterialRepository } from '@/repository/MaterialRepository';
import { Material, MaterialCategory } from '@/models/Materials';
import { getMaterialTypeLabel } from '@/utils/materialTypeLabel';

export interface GroupedMaterialOption {
	label: string;
	value: string;
}

export interface GroupedMaterials {
	label: string;
	options: GroupedMaterialOption[];
}

export class MaterialService {
	private repository: MaterialRepository;

	constructor(repository: MaterialRepository = new MaterialRepository()) {
		this.repository = repository;
	}

	getAll(): Material[] {
		return this.repository.findAll();
	}

	getMaterialByName(name: string): Material | undefined {
		return this.repository.findByName(name);
	}

	getMaterialById(id: string): Material | undefined {
		return this.repository.findById(id);
	}

	getMaterialsByCategory(category: MaterialCategory): Material[] {
		return this.repository.findByCategory(category);
	}

	getGroupedMaterialsByCategory(category: MaterialCategory): GroupedMaterials[] {
		const materials = this.getMaterialsByCategory(category);
		const grouped = materials.reduce<Record<string, GroupedMaterialOption[]>>((acc, material) => {
			const groupLabel = getMaterialTypeLabel(material.type);
			if (!acc[groupLabel]) {
				acc[groupLabel] = [];
			}
			acc[groupLabel].push({
				label: material.name,
				value: material.id,
			});
			return acc;
		}, {});

		return Object.keys(grouped)
			.sort((a, b) => a.localeCompare(b))
			.map((groupLabel) => ({
				label: groupLabel,
				options: grouped[groupLabel].sort((a, b) => a.label.localeCompare(b.label)),
			}));
	}

	getScoreByName(name: string): number {
		const material = this.repository.findByName(name);
		return material ? material.bioScore : 0;
	}

	getScoreById(id: string): number {
		const material = this.repository.findById(id);
		return material ? material.bioScore : 0;
	}
}

export const materialService = new MaterialService();
